import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Loader2,
  User,
  FileText,
  Wallet,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Upload,
  Edit,
  Shield,
  ChevronDown,
  RefreshCw
} from 'lucide-react';

const ActivityTimeline = ({ 
  userId, 
  entityType, 
  entityId, 
  isAdmin = false,
  limit = 10,
  showHeader = true,
  title = "Actividad Reciente",
  emptyMessage = "No hay actividad registrada"
}) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [visibleCount, setVisibleCount] = useState(limit);

  useEffect(() => {
    fetchActivities();
  }, [userId, entityType, entityId]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      let endpoint;
      if (entityType && entityId) {
        endpoint = `/admin/activities/entity/${entityType}/${entityId}`;
      } else if (isAdmin && userId) {
        endpoint = `/admin/activities/${userId}`;
      } else {
        endpoint = '/activities';
      }
      
      const response = await api.get(endpoint, { params: { limit: 100 } });
      setActivities(response.data.activities || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      user_registered: User,
      user_login: User,
      kyc_submitted: Shield,
      kyc_updated: Shield,
      kyc_approved: CheckCircle2,
      kyc_rejected: XCircle,
      kyc_more_info: AlertCircle,
      document_uploaded: Upload,
      document_deleted: FileText,
      document_requested: FileText,
      commitment_created: Wallet,
      commitment_updated: Wallet,
      commitment_approved: CheckCircle2,
      commitment_rejected: XCircle,
      commitment_confirmed: CheckCircle2,
      commitment_cancelled: XCircle,
      proof_uploaded: Upload,
      property_created: Building2,
      property_submitted: Building2,
      property_updated: Edit,
      property_status_change: Building2,
      property_approved: CheckCircle2,
      property_rejected: XCircle,
      opportunity_viewed: Building2,
      admin_action: Shield,
      system_event: Clock,
    };
    return icons[activityType] || Clock;
  };

  const getActivityColor = (activityType) => {
    if (activityType.includes('approved') || activityType.includes('confirmed') || activityType.includes('registered')) {
      return 'text-emerald-500 bg-emerald-500/10';
    }
    if (activityType.includes('rejected') || activityType.includes('cancelled') || activityType.includes('deleted')) {
      return 'text-red-500 bg-red-500/10';
    }
    if (activityType.includes('pending') || activityType.includes('more_info') || activityType.includes('requested')) {
      return 'text-amber-500 bg-amber-500/10';
    }
    if (activityType.includes('submitted') || activityType.includes('created') || activityType.includes('updated')) {
      return 'text-blue-500 bg-blue-500/10';
    }
    return 'text-muted-foreground bg-muted';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays} días`;
    
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const visibleActivities = activities.slice(0, visibleCount);
  const hasMore = activities.length > visibleCount;

  if (loading) {
    return (
      <Card className="card-dashboard">
        {showHeader && (
          <CardHeader>
            <CardTitle className="text-lg">{title}</CardTitle>
          </CardHeader>
        )}
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-dashboard">
      {showHeader && (
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{title}</CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchActivities} className="h-8 w-8 p-0">
            <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </CardHeader>
      )}
      <CardContent className={showHeader ? '' : 'pt-6'}>
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-8 w-8 mx-auto mb-3 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
            
            <div className="space-y-6">
              {visibleActivities.map((activity, index) => {
                const Icon = getActivityIcon(activity.activity_type);
                const colorClass = getActivityColor(activity.activity_type);
                
                return (
                  <div key={activity.activity_id} className="relative pl-12">
                    {/* Icon bubble */}
                    <div className={`absolute left-0 p-2 rounded-full ${colorClass}`}>
                      <Icon className="h-4 w-4" strokeWidth={1.5} />
                    </div>
                    
                    <div className="space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-medium">{activity.title}</h4>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(activity.created_at)}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground">{activity.description}</p>
                      
                      {activity.actor_name && activity.actor_type !== 'user' && (
                        <p className="text-xs text-muted-foreground">
                          Por: {activity.actor_name}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {hasMore && (
              <div className="mt-6 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setVisibleCount(prev => prev + 10)}
                  className="gap-2"
                >
                  <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
                  Ver más ({activities.length - visibleCount} restantes)
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityTimeline;
